import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileText, Image as ImageIcon, Paperclip, Trash2 } from 'lucide-react';
import {
  formatBytes,
  type Attachment,
  type AttachmentEntity,
} from '@amai/contracts';
import { api, download, upload } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { formatDate } from '@/lib/format';
import { T } from '@/design/tokens';
import { Button } from './Button';
import { ConfirmDialog } from './Modal';

/**
 * Anexos de um registro — a nota em PDF, o contrato, o comprovante do PIX.
 *
 * Mora numa aba do ContextPanel de quem for dono. A permissão não é declarada
 * aqui: a API cobra a da entidade dona, então quem edita a nota anexa nela e
 * quem só a consulta apenas baixa.
 */
export function Attachments({
  entity,
  entityId,
  canEdit,
}: {
  entity: AttachmentEntity;
  entityId: string;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [removing, setRemoving] = useState<Attachment | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['attachments', entity, entityId],
    queryFn: () =>
      api.get<{ data: Attachment[] }>('/attachments', { entity, entityId }),
    select: (response) => response.data,
  });

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: ['attachments', entity, entityId] });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.set('entity', entity);
      form.set('entityId', entityId);
      form.set('file', file);
      return upload<Attachment>('/attachments', form);
    },
    onSuccess: () => {
      setUploadError(null);
      refresh();
    },
    onError: (error) => setUploadError(errorMessage(error)),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/attachments/${id}`),
    onSuccess: () => {
      setRemoving(null);
      refresh();
    },
  });

  const downloadMutation = useMutation({
    mutationFn: (row: Attachment) =>
      download(`/attachments/${row.id}/download`, row.filename),
  });

  const rows = listQuery.data ?? [];

  return (
    <>
      {canEdit && (
        <>
          <input
            ref={fileInput}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.xml"
            style={{ display: 'none' }}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) uploadMutation.mutate(file);
              // Zera para o mesmo arquivo poder ser escolhido de novo.
              event.target.value = '';
            }}
          />
          <Button
            small
            icon={Paperclip}
            loading={uploadMutation.isPending}
            onClick={() => fileInput.current?.click()}
          >
            Anexar arquivo
          </Button>
          <p className="muted" style={{ fontSize: 12, marginTop: 6, color: T.inkFaint }}>
            PDF, foto (JPG, PNG, WEBP) ou XML.
          </p>
        </>
      )}

      {uploadError && (
        <div className="login__alert" role="alert" style={{ margin: '12px 0' }}>
          {uploadError}
        </div>
      )}

      {listQuery.isPending && <p className="muted" style={{ fontSize: 13 }}>Carregando…</p>}

      {rows.length === 0 && !listQuery.isPending && (
        <p className="muted" style={{ fontSize: 13, marginTop: 14 }}>
          Nada anexado ainda.
        </p>
      )}

      <div style={{ marginTop: 14 }}>
        {rows.map((row) => {
          const isImage = row.mimeType.startsWith('image/');
          return (
            <div
              key={row.id}
              className="card"
              style={{ padding: 10, marginBottom: 8, display: 'flex', gap: 10, alignItems: 'center' }}
            >
              {isImage ? (
                <ImageIcon size={16} color={T.mareDark} aria-hidden />
              ) : (
                <FileText size={16} color={T.mareDark} aria-hidden />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={row.filename}
                >
                  {row.filename}
                </div>
                <div className="mono" style={{ fontSize: 11.5, color: T.inkFaint, marginTop: 2 }}>
                  {formatBytes(row.sizeBytes)} · {formatDate(row.createdAt)}
                  {row.createdByName ? ` · ${row.createdByName}` : ''}
                </div>
              </div>
              <Button
                small
                variant="ghost"
                icon={Download}
                aria-label={`Baixar ${row.filename}`}
                title="Baixar"
                loading={downloadMutation.isPending && downloadMutation.variables?.id === row.id}
                onClick={() => downloadMutation.mutate(row)}
              />
              {canEdit && (
                <Button
                  small
                  variant="ghost"
                  icon={Trash2}
                  aria-label={`Excluir ${row.filename}`}
                  title="Excluir o anexo"
                  onClick={() => setRemoving(row)}
                />
              )}
            </div>
          );
        })}
      </div>

      {downloadMutation.isError && (
        <div className="login__alert" role="alert" style={{ marginTop: 12 }}>
          {errorMessage(downloadMutation.error)}
        </div>
      )}

      <ConfirmDialog
        open={Boolean(removing)}
        destructive
        loading={removeMutation.isPending}
        title="Excluir este anexo?"
        confirmLabel="Excluir"
        error={removeMutation.error ? errorMessage(removeMutation.error) : undefined}
        message={
          <>
            <strong>{removing?.filename}</strong> sai do sistema e o arquivo é
            apagado do servidor. Isso não tem volta — se for a única cópia,
            baixe antes.
          </>
        }
        onClose={() => {
          setRemoving(null);
          removeMutation.reset();
        }}
        onConfirm={() => removing && removeMutation.mutate(removing.id)}
      />
    </>
  );
}
